/** @format */

import {
	AddressV2,
	AthleteManagementAddressResponse,
	Environment
} from '@shared/data-access/interfaces';
import { AthleteProfile, IdentityV2, PhoneV2 } from '@my-account/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';

import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { HttpHeaders } from '@angular/common/http';
import { HttpService } from '@shared/data-access/services';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

/* istanbul ignore file */

@Injectable({
	providedIn: 'root'
})
export class AthleteManagementDao {
	private environment = inject(Environment);
	constructor(private http: HttpService) {}

	// Default HTTP Header properties
	private DEFAULT_HTTP_HEADER_PROPERTIES = {
		Accept: MediaType.APPLICATION_JSON,
		'Content-Type': MediaType.APPLICATION_JSON,
		'Access-Control-Allow-Origin': '*',
		'Cache-Control': 'no-cache,no-store,must-revalidate,max-age=0',
		Pragma: 'no-cache',
		Expires: 'Sat, 01 Jan 2000 00:00:00 GMT',
		partner_key: this.environment.identityServiceKey.partnerKey,
		secret_key: this.environment.identityServiceKey.secretKey
	};

	// Default HTTP Headers object
	private DEFAULT_HTTP_HEADERS = new HttpHeaders(this.DEFAULT_HTTP_HEADER_PROPERTIES);

	// Default number of HTTP retries
	private DEFAULT_HTTP_RETRIES = 2;

	/**
	 * @returns {Observable<IdentityV2>}
	 *
	 * @description
	 * Function that calls the Athlete Management Service update email API. This endpoint will accept an email as a string
	 * and return an updated identity object
	 * @param email
	 */

	public updateEmail(email: string): Observable<IdentityV2> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/email`;
		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		// Make the HTTP Request
		return this.http
			.put<IdentityV2>(url, { email }, httpOptions)
			.pipe(retry(this.DEFAULT_HTTP_RETRIES));
	}

	/**
	 * @returns {Observable<AthleteManagementAddressResponseModel>}
	 *
	 * @description
	 * Function that calls the Athlete Management Service create address API. This endpoint will accept an address
	 * and return an addressId
	 * @param address
	 */
	public createAddress(address: AddressV2): Observable<AthleteManagementAddressResponse> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/address`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		// Make the HTTP Request
		return this.http
			.put<AthleteManagementAddressResponse>(url, address, httpOptions)
			.pipe(retry(this.DEFAULT_HTTP_RETRIES));
	}

	/**
	 * @returns {Observable<AthleteManagementAddressResponseModel>}
	 *
	 * @description
	 * Function that calls the Athlete Management Service update address API. This endpoint will accept an address
	 * and return an addressId
	 * @param address
	 */
	public updateAddress(address: AddressV2): Observable<AthleteManagementAddressResponse> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/address/${address.addressId}`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		// Make the HTTP Request
		return this.http
			.put<AthleteManagementAddressResponse>(url, address, httpOptions)
			.pipe(retry(this.DEFAULT_HTTP_RETRIES));
	}

	/**
	 * @returns {Observable<string>}
	 *
	 * @description
	 * Function that calls the Athlete Management Service delete address API. This endpoint will accept an address id in its url
	 * and return a string
	 * @param addressId
	 */
	public deleteAddress(addressId: string): Observable<string> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/address/${addressId}`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		// Make the HTTP Request
		return this.http.delete<string>(url, httpOptions).pipe(retry(this.DEFAULT_HTTP_RETRIES));
	}

	/**
	 * @returns {Observable<IdentityV2>}
	 *
	 * @description
	 * Function that calls the Athlete Management Service get identity API. This endpoint will return the identity object
	 */

	public getIdentity(): Observable<IdentityV2> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/identity`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		return this.http.get<IdentityV2>(url, this.DEFAULT_HTTP_RETRIES, httpOptions);
	}

	/**
	 * @returns {Observable<IdentityV2>}
	 *
	 * @description
	 * Function that calls the Athlete Management Service update identity API. This endpoint will accept a first name and last name in string form
	 * and return an updated identity object
	 * @param email
	 */
	public updateIdentity(firstName: string, lastName: string): Observable<IdentityV2> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/identity`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		return this.http
			.put<IdentityV2>(url, { firstName, lastName }, httpOptions)
			.pipe(retry(this.DEFAULT_HTTP_RETRIES));
	}

	/**
	 * @returns {Observable<IdentityV2>}
	 * @description
	 * patches the phone number and returns identity
	 * @param phone
	 */
	public patchPhone(phone: PhoneV2): Observable<IdentityV2> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/phone`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		return this.http.patch<IdentityV2>(url, phone, httpOptions);
	}

	public getAddress(): Observable<AddressV2[]> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/address`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		return this.http
			.get<AddressV2[]>(url, 0, httpOptions)
			.pipe(retry(this.DEFAULT_HTTP_RETRIES));
	}

	/**
	 * @returns {Observable<any>}
	 * @description
	 * Updates athlete profile for page 2.
	 * @param athleteProfile
	 * @param shouldIncentive
	 */
	public updateAthleteProfile(
		athleteProfile: AthleteProfile,
		shouldIncentive = false
	): Observable<any> {
		const url = `${this.environment.baseUrls.athleteManagementService}/v1/athlete/profile`;

		// HTTP Options
		const httpOptions = {
			headers: this.DEFAULT_HTTP_HEADERS,
			withCredentials: true
		};

		// Override default Timeout specified in HttpInterceptor
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.X_TIMEOUT,
			`${DCSG_CONSTANTS.timeouts.medium}`
		);

		// set the loyalty pii incentive header
		httpOptions.headers = httpOptions.headers.set(
			CustomHttpHeaders.LOYALTY_INCENTIVE_HEADER,
			`${shouldIncentive}`
		);

		return this.http
			.put<void>(url, athleteProfile, httpOptions)
			.pipe(retry(this.DEFAULT_HTTP_RETRIES));
	}
}
